export const TEXT_SELECTOR = "p, h1, h2, h3, h4, h5, h6, li, blockquote, figcaption, dd, td";
export const MIN_TEXT = 12;

export const HOST_TAG = "aimarker-root";
export const TAG_TAG = "aimarker-tag";

export function isOwn(node: Element | null): boolean {
  return !!node?.closest?.(`${HOST_TAG}, ${TAG_TAG}`);
}

export function textOf(el: Element): string {
  return el.textContent ?? "";
}

/** Text blocks that don't contain other text blocks (avoid double-marking li > p). */
export function textBlocks(root: ParentNode = document): Element[] {
  const all = Array.from(root.querySelectorAll(TEXT_SELECTOR));
  return all.filter((el) => !isOwn(el) && !el.querySelector(TEXT_SELECTOR) && textOf(el).trim().length >= MIN_TEXT);
}

export function images(root: ParentNode = document): HTMLImageElement[] {
  return Array.from(root.querySelectorAll("img")).filter((img) => !isOwn(img) && (img.currentSrc || img.src));
}

export const imgSrc = (img: HTMLImageElement) => img.currentSrc || img.src;

const SKIP_BG = new Set(["HTML", "HEAD", "SCRIPT", "STYLE", "LINK", "META", "NOSCRIPT", "SVG", "PATH", "IMG", "VIDEO", "IFRAME"]);

/** First url(...) of an element's computed CSS background-image (ignores pure gradients). */
export function bgUrl(el: Element): string | null {
  if (SKIP_BG.has(el.tagName.toUpperCase())) return null;
  const bg = getComputedStyle(el).backgroundImage;
  if (!bg || bg === "none" || !bg.includes("url(")) return null;
  const m = bg.match(/url\((['"]?)(.*?)\1\)/);
  return m && m[2] && !m[2].startsWith("data:") ? m[2] : null;
}

/** Elements rendering a CSS background image (hero sections, sliders, cards). Capped for huge DOMs. */
export function bgElements(root: ParentNode = document, cap = 6000): Element[] {
  const out: Element[] = [];
  const all = root.querySelectorAll("body *");
  for (let i = 0; i < all.length && i < cap; i++) {
    const el = all[i];
    if (!isOwn(el) && bgUrl(el)) out.push(el);
  }
  return out;
}

/** Image source for an <img> or a background-image element. */
export function imageSrcOf(el: Element): string | null {
  return el instanceof HTMLImageElement ? imgSrc(el) : bgUrl(el);
}

function cssEscape(s: string) {
  return typeof CSS !== "undefined" && CSS.escape ? CSS.escape(s) : s.replace(/[^a-zA-Z0-9_-]/g, "\\$&");
}

/** Short, reasonably stable CSS path. Used as a hint; matching relies on fingerprints. */
export function selectorFor(el: Element): string {
  const parts: string[] = [];
  let node: Element | null = el;
  while (node && node.nodeType === 1 && node !== document.documentElement && parts.length < 6) {
    if (node.id && document.querySelectorAll(`#${cssEscape(node.id)}`).length === 1) {
      parts.unshift(`#${cssEscape(node.id)}`);
      break;
    }
    const tag = node.tagName.toLowerCase();
    const parent: Element | null = node.parentElement;
    if (!parent) {
      parts.unshift(tag);
      break;
    }
    const same = Array.from(parent.children).filter((c) => c.tagName === node!.tagName);
    parts.unshift(same.length > 1 ? `${tag}:nth-of-type(${same.indexOf(node) + 1})` : tag);
    node = parent;
  }
  return parts.join(" > ");
}

export function preview(el: Element): string {
  if (el instanceof HTMLImageElement) return el.alt?.trim() || imgSrc(el);
  const bg = !el.matches(TEXT_SELECTOR) && bgUrl(el);
  if (bg) return el.getAttribute("aria-label")?.trim() || el.getAttribute("title")?.trim() || bg;
  return textOf(el).replace(/\s+/g, " ").trim().slice(0, 240);
}

/** Main headline of the page: the article label goes right above it (EU guidance: above/near the headline). */
export function findHeadline(root: ParentNode = document): Element | null {
  const candidates = root.querySelectorAll("main h1, article h1, [role=main] h1, h1, main h2, article h2");
  for (const el of Array.from(candidates)) {
    if (isOwn(el)) continue;
    const r = el.getBoundingClientRect();
    if (r.width > 0 && r.height > 0 && textOf(el).trim()) return el;
  }
  return textBlocks(root)[0] ?? null;
}

function rgba(c: string): [number, number, number, number] | null {
  const m = c.match(/rgba?\(([^)]+)\)/);
  if (!m) return null;
  const p = m[1]!.split(/[\s,/]+/).filter(Boolean).map(parseFloat);
  return [p[0] ?? 0, p[1] ?? 0, p[2] ?? 0, p[3] ?? 1];
}

function luminance([r, g, b]: [number, number, number, number]) {
  const f = (v: number) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
}

/**
 * Is the area around `el` dark? Uses the first opaque background colour up the tree; when a background
 * image/gradient comes first (hero photos), trusts the text colour the designer chose for that area.
 */
export function isDarkSurface(el: Element): boolean {
  const text = rgba(getComputedStyle(el).color);
  for (let n: Element | null = el; n && n !== document.documentElement; n = n.parentElement) {
    const cs = getComputedStyle(n);
    if (cs.backgroundImage && cs.backgroundImage !== "none") return text ? luminance(text) > 0.4 : false;
    // Overlays drawn with ::before/::after (common on hero sections)
    for (const pseudo of ["::before", "::after"]) {
      const ps = getComputedStyle(n, pseudo);
      if (ps.content !== "none" && ps.position === "absolute" && (ps.backgroundImage !== "none" || (rgba(ps.backgroundColor)?.[3] ?? 0) > 0.4)) return text ? luminance(text) > 0.4 : false;
    }
    const bg = rgba(cs.backgroundColor);
    if (bg && bg[3] > 0.5) return luminance(bg) < 0.25;
  }
  const body = rgba(getComputedStyle(document.body).backgroundColor);
  if (body && body[3] > 0.5) return luminance(body) < 0.25;
  return text ? luminance(text) > 0.4 : false;
}
